const REDACTED = '[redacted]';

// Match encoded equivalents of fixed route names without decoding credential
// values: decoding an encoded space or delimiter can expose the token tail.
function encodedLiteral(value: string): string {
  return [...value].map(character => {
    const literal = character.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const encoded = character.charCodeAt(0).toString(16).padStart(2, '0');
    const upper = character.toUpperCase().charCodeAt(0).toString(16).padStart(2, '0');
    return `(?:${literal}|%${encoded}|%${upper})`;
  }).join('');
}

const capabilityPaths = [
  'signet/invitations', 'sign/invite', 'resident-access/invites', 'property-requests',
  'vendor-jobs', 'vendor-bids', 'maintenance-assessments', 'maintenance-vendor',
  'maintenance-fixed-check', 'owner-approvals', 'deposit-dispositions', 'inspections',
  'move-in-label-captures', 'vendor/jobs', 'vendor/bids', 'vendor/assessments',
  'vendor/maintenance', 'maintenance/fixed-check', 'owner-approval',
  'deposit-disposition', 'inspection', 'move-in', 'access', 'register', 'apply', 'vendor-papers', 'vendor/papers',
];
const credentialValue = String.raw`[^\s/?#"'<>\\]+`;
const PROPERTY_CAPABILITY_PATH = new RegExp(
  `(${capabilityPaths.map(path => encodedLiteral(`/${path}/`)).join('|')})${credentialValue}`, 'gi',
);
const PUBLIC_MAINTENANCE_PATH = new RegExp(
  String.raw`(^|https?:\/\/[^\s/?#"'<>\\]+|[\s"'(=])(${encodedLiteral('/maintenance/')})(?!${encodedLiteral('fixed-check/')})${credentialValue}`, 'gi',
);
const CREDENTIAL_QUERY = new RegExp(
  `((?:^|[?&])(?:${['token', 'access_token', 'refresh_token', 'session_token', 'code', 'authorization'].map(encodedLiteral).join('|')})=)[^\\s&#"'<>\\\\]*`, 'gi',
);
const CREDENTIAL_KEY = /token|authorization|cookie|password|secret|api.?key/i;

export function redactPropertyCapabilityText(value: string): string {
  return value
    .replace(PROPERTY_CAPABILITY_PATH, `$1${REDACTED}`)
    .replace(PUBLIC_MAINTENANCE_PATH, `$1$2${REDACTED}`)
    .replace(CREDENTIAL_QUERY, `$1${REDACTED}`);
}

// Telemetry is a JSON-shaped copy, never the live request/event object.
export function scrubPropertyTelemetry<T>(value: T): T {
  const seen = new WeakSet<object>();
  function scrub(entry: unknown, depth: number, parentKey = ''): unknown {
    if (depth > 12) return REDACTED;
    if (typeof entry === 'string') return redactPropertyCapabilityText(entry);
    if (entry === null || typeof entry !== 'object') return entry;
    if (seen.has(entry)) return REDACTED;
    seen.add(entry);
    if (Array.isArray(entry)) return entry.map(item => scrub(item, depth + 1));
    return Object.fromEntries(Object.entries(entry).map(([key, item]) => [
      key, CREDENTIAL_KEY.test(key) || (parentKey === 'request' && ['data', 'query_string'].includes(key))
        ? REDACTED : scrub(item, depth + 1, key),
    ]));
  }
  return scrub(value, 0) as T;
}

/**
 * Whether a process reports to Sentry. Local and test stacks share the production project, so they stay silent unless
 * someone opts in to debug the reporting itself.
 */
export function shouldReportToSentry(input: { dsn?: string; environment?: string; enableDev?: string }): boolean {
  if (!input.dsn?.trim()) return false;
  const environment = input.environment?.trim();
  return (environment !== 'development' && environment !== 'test') || input.enableDev?.trim() === '1';
}
